<template>
  <div :class="{'is-invalid': validationErrors.length > 0}">
    <label v-if="displayLabel" class="col-form-label" for="location-query-field">
      <required v-if="required" />
      {{ label }}
    </label>
    <custom-gmap-autocomplete id="location-query-field" name="query"
                              :value="locationQuery"
                              :select-first-on-enter="true"
                              :class="['form-control query-input', inputClass]"
                              :options="autocompleteOptions"
                              :placeholder="placeholder"
                              @input="updateQuery"
                              @place-changed="autocompletePlaceChanged"
                              @focus="focusAutocomplete" />
    <validation-errors :errors="validationErrors" />

    <hidden-fields :field-group-name="name" :location="location" />
  </div>
</template>

<script>
import CustomGmapAutocomplete from '../custom-gmap-autocomplete'
import GeocodeLocation from '@/mixins/geocode-location'
import HiddenFields from './hidden-fields'
import { inject } from 'vue'
import isPresent from '@/helpers/is-present'
import { nextTick } from 'vue'
import Required from '../../required'
import ValidationErrors from '../validation-errors'

export default {
  components: { CustomGmapAutocomplete, HiddenFields, Required, ValidationErrors },

  mixins: [ GeocodeLocation ],

  props: {
    boundaryBox: {
      type: Object,
      required: false
    },
    country: {
      // Google accepts either a string an array of countries to filter results to
      type: [String, Array],
      required: false,
      default: null
    },
    excludeVenueField: {
      type: Boolean,
      default: false
    },
    formId: {
      type: String,
      required: true
    },
    initialLocation: {
      type: Object,
      required: false
    },
    inputClass: {
      type: String,
      required: false,
      default: ''
    },
    label: {
      type: String,
      required: false,
      default() { return inject('i18n').t('where') }
    },
    name: {
      type: String,
      required: false,
      default: 'location'
    },
    placeholder: {
      type: String,
      required: false
    },
    required: {
      type: Boolean,
      required: false,
      default: false
    },
    validationErrors: {
      type: Array,
      required: false,
      default: () => { return [] }
    }
  },

  emits: ['focus-autocomplete', 'location-selected'],

  data() {
    return {
      form: null,
      location: this.initialLocation,
      currentQuery: null,
      displayLabel: isPresent(this.label)
    }
  },


  computed: {
    // If there is a location loaded already, for eg from inititalLocation use it's query. Otherwise, use
    // whatever the user has typed into the field
    locationQuery() {
      if (this.location && this.location.query) {
        if (this.currentQuery === null) {
          return this.location.query
        } else {
          return this.currentQuery
        }
      }

      return this.currentQuery || ''
    }
  },

  mounted() {
    this.form = document.getElementById(this.formId)
    this.form.addEventListener('submit', this.handleFormSubmit)
  },

  methods: {
    // This method is required by autocompletePlaceChanged
    changeAttribute() {
      this.$emit('location-selected', this.location)
      this.updateQuery(this.location.query)
    },

    handleFormSubmit(evt) {
      evt.preventDefault()

      // skip geocoding if the query in the input has already been geocoded from autocomplete.
      if (this.locationQuery === this.location.query && this.location.latitude) {
        this.form.submit()
      } else {
        this.geocodeLocation(() => {
          if (this.geocodeSucceeded) {
            // We need to wait for the next tick so vue has a chance to render the hidden fields on to the form
            // before we submit it.
            nextTick(() => {
              this.form.submit()
            })
          }
        })
      }
    },

    updateQuery(newQuery) {
      this.currentQuery = newQuery
    },

    focusAutocomplete() {
      this.$emit('focus-autocomplete')
    }
  }
}
</script>
